import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Course } from '../types';
import { supabase } from '../lib/supabase';

interface CourseContextType {
  courses: Course[];
  loading: boolean;
  error: string | null;
  addCourse: (course: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'currentApplicants'>) => Promise<void>;
  updateCourse: (id: string, course: Partial<Course>) => Promise<void>;
  deleteCourse: (id: string) => Promise<void>;
  getCourse: (id: string) => Course | undefined;
  refreshCourses: () => Promise<void>;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 프로그램 데이터 가져오기
  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching courses from Supabase...');
      const { data, error: fetchError } = await supabase
        .from('programs')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Supabase response:', { data, error: fetchError });

      if (fetchError) {
        console.error('Supabase fetch error:', fetchError);
        throw fetchError;
      }

      // View에서 프로그램별 신청자 수 조회
      const { data: statsData } = await supabase
        .from('program_application_stats')
        .select('program_id, registered_count, waiting_count');

      // 프로그램별 접수/대기 통계를 별도로 저장
      const registeredMap = Object.fromEntries(
        (statsData || []).map(stat => [stat.program_id, stat.registered_count || 0])
      );
      const waitingMap = Object.fromEntries(
        (statsData || []).map(stat => [stat.program_id, stat.waiting_count || 0])
      );
      const countMap = Object.fromEntries(
        (statsData || []).map(stat => [
          stat.program_id,
          (stat.registered_count || 0) + (stat.waiting_count || 0)
        ])
      );

      // Supabase 데이터를 Course 타입으로 변환
      const transformedCourses: Course[] = (data || []).map((program: any) => ({
        id: program.id,
        courseName: program.name,
        instructor: program.instructor,
        status: program.status,
        status_mode: program.status_mode || 'auto',
        category: program.category,
        registrationStartDate: program.registration_start_date,
        registrationEndDate: program.registration_end_date,
        registrationStartTime: program.registration_start_time || '00:00',
        registrationEndTime: program.registration_end_time || '23:59',
        courseStartDate: program.course_start_date,
        courseEndDate: program.course_end_date,
        currentApplicants: countMap[program.id] || 0,
        registeredCount: registeredMap[program.id] || 0,
        waitingApplicants: waitingMap[program.id] || 0,
        maxStudents: program.max_students,
        waitingList: program.waiting_list || 0,
        tuition: program.tuition || '',
        materialFee: program.material_fee || '',
        selectionMethod: program.selection_method || 'first-come',
        courseDescription: program.description,
        courseCycle: program.course_cycle || 'weekly',
        courseDays: program.course_days || [],
        courseStartTime: program.course_start_time || '09:00',
        courseEndTime: program.course_end_time || '18:00',
        courseTime: program.course_start_time && program.course_end_time
          ? `${program.course_start_time}-${program.course_end_time}`
          : '',
        totalSessions: program.total_sessions || 0,
        semester: program.semester,
        grade: program.grade,
        courseLevel: program.course_level,
        paymentDeadline: program.payment_deadline,
        refundDeadline: program.refund_deadline,
        remarks: program.remarks,
        schedule: program.schedule,
        institution: '동두천 글로벌인재센터',
        location: program.location || '',
        contactPerson: program.contact_person || '동두천시 글로벌인재센터',
        contactPhone: program.contact_phone || '031-863-3522',
        courseImage: program.image_url,
        is_published: program.is_published ?? true,
        display_order: program.display_order,
        // 노출 제어 필드 (2025-01-27 추가)
        show_in_catalog: program.show_in_catalog ?? false,
        show_in_registration: program.show_in_registration ?? false,
        catalog_order: program.catalog_order,
        registration_order: program.registration_order,
        allow_discount: program.allow_discount ?? true,
        createdAt: program.created_at,
        updatedAt: program.updated_at
      }));

      setCourses(transformedCourses);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchCourses();
  }, []);

  const addCourse = async (courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'currentApplicants'>) => {
    try {
      setError(null);

      // 빈 문자열을 null로 변환하는 헬퍼 함수
      const emptyToNull = (val: string | undefined | null) => (val === '' || val === undefined) ? null : val;

      // Course 타입을 Supabase programs 타입으로 변환
      const programData = {
        name: courseData.courseName,
        category: courseData.category || 'adult',
        description: courseData.courseDescription,
        instructor: courseData.instructor,
        max_students: courseData.maxStudents,
        waiting_list: courseData.waitingList || 0,
        tuition: emptyToNull(courseData.tuition),
        material_fee: emptyToNull(courseData.materialFee),
        registration_start_date: emptyToNull(courseData.registrationStartDate),
        registration_end_date: emptyToNull(courseData.registrationEndDate),
        registration_start_time: courseData.registrationStartTime || '00:00:00',
        registration_end_time: courseData.registrationEndTime || '23:59:59',
        course_start_date: emptyToNull(courseData.courseStartDate),
        course_end_date: emptyToNull(courseData.courseEndDate),
        course_start_time: courseData.courseStartTime || '09:00:00',
        course_end_time: courseData.courseEndTime || '18:00:00',
        course_days: courseData.courseDays || [],
        location: courseData.location || null,
        selection_method: courseData.selectionMethod || 'first-come',
        status: courseData.status || 'recruiting',
        status_mode: courseData.status_mode || 'auto',
        image_url: emptyToNull(courseData.courseImage),
        // Optional fields
        semester: emptyToNull(courseData.semester),
        grade: emptyToNull(courseData.grade),
        course_level: emptyToNull(courseData.courseLevel),
        course_cycle: emptyToNull(courseData.courseCycle),
        total_sessions: courseData.totalSessions,
        payment_deadline: emptyToNull(courseData.paymentDeadline),
        refund_deadline: emptyToNull(courseData.refundDeadline),
        contact_person: courseData.contactPerson || '동두천시 글로벌인재센터',
        contact_phone: courseData.contactPhone || '031-863-3522',
        remarks: emptyToNull(courseData.remarks),
        schedule: emptyToNull(courseData.schedule),
        is_published: courseData.is_published,
        // 노출 제어 필드 (2025-01-27 추가)
        show_in_catalog: courseData.show_in_catalog ?? false,
        show_in_registration: courseData.show_in_registration ?? false,
        catalog_order: courseData.catalog_order,
        registration_order: courseData.registration_order,
        allow_discount: courseData.allow_discount ?? true
      };

      console.log('=== CourseContext addCourse ===');
      console.log('courseData.courseDescription:', courseData.courseDescription);
      console.log('programData.description:', programData.description);
      console.log('===============================');

      const { error: insertError } = await supabase
        .from('programs')
        .insert(programData);

      if (insertError) throw insertError;

      // 데이터 다시 불러오기
      await fetchCourses();
    } catch (err) {
      console.error('Error adding course:', err);
      setError(err instanceof Error ? err.message : '프로그램 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateCourse = async (id: string, courseData: Partial<Course>) => {
    try {
      setError(null);

      console.log('=== CourseContext updateCourse ===');
      console.log('courseData.courseDescription:', courseData.courseDescription);
      console.log('==================================');

      // Course 타입을 Supabase programs 타입으로 변환
      const updateData: any = {};
      if (courseData.courseName) updateData.name = courseData.courseName;
      if (courseData.category) updateData.category = courseData.category;
      if (courseData.courseDescription) updateData.description = courseData.courseDescription;
      if (courseData.instructor) updateData.instructor = courseData.instructor;
      if (courseData.maxStudents !== undefined) updateData.max_students = courseData.maxStudents;
      if (courseData.waitingList !== undefined) updateData.waiting_list = courseData.waitingList;
      if (courseData.tuition !== undefined) updateData.tuition = courseData.tuition || null;
      if (courseData.materialFee !== undefined) updateData.material_fee = courseData.materialFee || null;
      if (courseData.registrationStartDate) updateData.registration_start_date = courseData.registrationStartDate;
      if (courseData.registrationEndDate) updateData.registration_end_date = courseData.registrationEndDate;
      if (courseData.registrationStartTime) updateData.registration_start_time = courseData.registrationStartTime;
      if (courseData.registrationEndTime) updateData.registration_end_time = courseData.registrationEndTime;
      if (courseData.courseStartDate) updateData.course_start_date = courseData.courseStartDate;
      if (courseData.courseEndDate) updateData.course_end_date = courseData.courseEndDate;
      if (courseData.courseStartTime) updateData.course_start_time = courseData.courseStartTime;
      if (courseData.courseEndTime) updateData.course_end_time = courseData.courseEndTime;
      if (courseData.courseDays) updateData.course_days = courseData.courseDays;
      if (courseData.location !== undefined) updateData.location = courseData.location;
      if (courseData.selectionMethod) updateData.selection_method = courseData.selectionMethod;
      if (courseData.status) updateData.status = courseData.status;
      if (courseData.status_mode !== undefined) updateData.status_mode = courseData.status_mode;
      if (courseData.courseImage) updateData.image_url = courseData.courseImage;
      // Optional fields
      if (courseData.semester !== undefined) updateData.semester = courseData.semester;
      if (courseData.grade !== undefined) updateData.grade = courseData.grade;
      if (courseData.courseLevel !== undefined) updateData.course_level = courseData.courseLevel;
      if (courseData.courseCycle !== undefined) updateData.course_cycle = courseData.courseCycle;
      if (courseData.totalSessions !== undefined) updateData.total_sessions = courseData.totalSessions;
      if (courseData.paymentDeadline !== undefined) updateData.payment_deadline = courseData.paymentDeadline;
      if (courseData.refundDeadline !== undefined) updateData.refund_deadline = courseData.refundDeadline;
      if (courseData.contactPerson) updateData.contact_person = courseData.contactPerson;
      if (courseData.contactPhone) updateData.contact_phone = courseData.contactPhone;
      if (courseData.remarks !== undefined) updateData.remarks = courseData.remarks;
      if (courseData.schedule !== undefined) updateData.schedule = courseData.schedule;
      if (courseData.is_published !== undefined) updateData.is_published = courseData.is_published;
      // 노출 제어 필드 (2025-01-27 추가)
      if (courseData.show_in_catalog !== undefined) updateData.show_in_catalog = courseData.show_in_catalog;
      if (courseData.show_in_registration !== undefined) updateData.show_in_registration = courseData.show_in_registration;
      if (courseData.catalog_order !== undefined) updateData.catalog_order = courseData.catalog_order;
      if (courseData.registration_order !== undefined) updateData.registration_order = courseData.registration_order;
      if (courseData.allow_discount !== undefined) updateData.allow_discount = courseData.allow_discount;

      console.log('updateData.description:', updateData.description);
      console.log('Sending to Supabase:', updateData);

      const { error: updateError } = await supabase
        .from('programs')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      // 데이터 다시 불러오기
      await fetchCourses();
    } catch (err) {
      console.error('Error updating course:', err);
      setError(err instanceof Error ? err.message : '프로그램 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteCourse = async (id: string) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('programs')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // 로컬 상태에서 제거
      setCourses(prev => prev.filter(course => course.id !== id));
    } catch (err) {
      console.error('Error deleting course:', err);
      setError(err instanceof Error ? err.message : '프로그램 삭제에 실패했습니다.');
      throw err;
    }
  };

  const getCourse = (id: string) => {
    return courses.find(course => course.id === id);
  };

  const refreshCourses = async () => {
    await fetchCourses();
  };

  return (
    <CourseContext.Provider value={{
      courses,
      loading,
      error,
      addCourse,
      updateCourse,
      deleteCourse,
      getCourse,
      refreshCourses
    }}>
      {children}
    </CourseContext.Provider>
  );
};

export const useCourses = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourses must be used within a CourseProvider');
  }
  return context;
};